import { Box, ButtonProps, useMediaQuery, useTheme } from "@mui/material";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { COLORS } from "../../assets/theme";
import AutoComplete from "../../components/common/AutoComplete";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import {
  createSOA,
  pdfSOASApi,
  regenerateSOASApi,
  sendSingleSoaEmail,
  sendSoaEmails,
  SOAType,
  useSOAQuery,
} from "../../services/SOAService";
import { DateRange, IField, Params } from "../../types/table/interfaces.type";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import CustomDateTimePicker from "../../components/common/CustomDateTimePicker";
import config from "../../config.json";
import { pageAsOptions } from "../../utils/commonDataUtils";
import { fetchAccounts } from "../../services/AccountService";
import {
  fetchProfile,
  useProfilesDropdown,
  useProfilesQuery,
} from "../../services/ProfilesService";
import Actions from "../../components/common/Actions";
import ToastNotification from "../../components/ToastNotification";
import { fetchCurrency } from "../../services/CurrencyService";
import { useQueryClient } from "@tanstack/react-query";
import SendSOAEmailDialog from "../../components/SendSOAEmailDialog";
import History from "../../components/History";
import { hasJwtPermission } from "../../services/ServicesHelpers";

interface Option {
  label: string;
  value: string;
}
type DialogSOAData = Omit<SOAType, "profileId"> & {
  profileId?: string[];
  initialDateSao?: Date | null;
};
// Matches the backend default (soa.initial-date in admin-api application.properties).
const DEFAULT_INITIAL_DATE_SAO = new Date(Date.UTC(2026, 9, 11));

const InitialDatePicker: React.FC<{
  value: Date | null;
  onChange: (date: Date | null) => void;
}> = ({ value, onChange }) => (
  <CustomDateTimePicker
    label="Initial Date"
    value={value}
    onChange={onChange}
    WitOutTime={true}
    goldTheme={true}
    placeholder="Select Initial Date"
    helperText="Initial balance and all billings created before this date are merged into the first row."
  />
);
const SOAPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [filters, setFilters] = useState<Params<SOAType>>({});
  const [appliedFilters, setAppliedFilters] = useState<
    Params<SOAType & DateRange>
  >({
    search: "",
  });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [profileOptions, setProfileOptions] = useState<Option[]>([]);
  const [accountOptions, setAccountOptions] = useState<Option[]>([]);
  const [profileMap, setProfileMap] = useState<
    Record<
      string,
      { name: string; accountManagerEmail: string; currencyId: string }
    >
  >({});
  const [filterDeleted, setFilterDeleted] = useState(false);
  const [regenerateId, setRegenerateId] = useState<string | null>(null);
  const [regenerateDate, setRegenerateDate] = useState<Date | null>(null);

  const { data: profileOption } = useProfilesQuery();
  const { data: dataProfiledropdown = [] } = useProfilesDropdown(filterDeleted);

  const dialogRef = useRef<SOADialogRef>(null);

  const handleOpenDialog = (mode: "add" | "send" | "history", id?: string) => {
    dialogRef.current?.openDialog(mode);
  };

  useEffect(() => {
    if (profileOption) {
      setProfileOptions(profileOption.profileOptions);
      setProfileMap(profileOption.profileMap);
    }
    const loadInitialData = async () => {
      try {
        const accountsPage = await fetchAccounts({
          sort: "name",
          size: config.MAX_PAGE_SIZE,
        });
        const accountsAsOptions = pageAsOptions(accountsPage).map((option) => ({
          ...option,
          value: String(option.value),
        }));
        setAccountOptions(accountsAsOptions);
      } catch (error: any) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
  }, [profileOption]);

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      profileId: filters.profileId,
      excludeZero: filters.excludeZero,
    }));
  };

  const autoCompletes = [
    {
      label: "Account Profile",
      value: filters.profileId || "",
      options: dataProfiledropdown,
      isDeleted: filterDeleted,
      clearable: true,
      onToggleDeleted: () => setFilterDeleted((prev) => !prev),
      placeholder: "Select Profile",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProfileOption = dataProfiledropdown.find(
          (option) => option.value === value
        );
        setFilters((prev) => ({
          ...prev,
          profileId: String(selectedProfileOption?.value || ""),
        }));
      },
    },
  ];

  const dropdowns = [
    {
      label: "Exclude Zero",
      value:
        filters.excludeZero === undefined ? "" : String(filters.excludeZero),
      options: [
        { label: "Exclude Zero", value: "true" },
        { label: "Don't Exclude Zero", value: "false" },
      ],
      onChange: (value: string | number | (string | number)[]) => {
        if (Array.isArray(value)) return;
        setFilters((prev) => ({
          ...prev,
          excludeZero:
            value === "true" ? true : value === "false" ? false : undefined,
        }));
      },
    },
  ];

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_SOAS",
    },
  ];
  const actions = Actions({
    onPdf: {
      handler: async (id: string | number) => {
        await pdfSOASApi(String(id));
      },
      isDisabled: !hasJwtPermission("GET_SOA_PDF"),
    },
    reGenerate: {
      handler: async (id: string | number) => {
        setRegenerateDate(DEFAULT_INITIAL_DATE_SAO);
        setRegenerateId(String(id));
      },
      isDisabled: (row: any) => row.status === "SENT" || !hasJwtPermission("REGENERATE_SOA"),
    },
    // onViewHistory: {
    //   // isDisabled: true,
    //   handler: async (id: number | string) => {
    //     handleOpenDialog("history", String(id));
    //   },
    // },
  });

  const fields = useMemo<IField<SOAType>[]>(() => [
    { key: "createdDate", header: "Created Date" },
    {
      key: "profileId",
      header: "Account Profile",
      transform: async (t) => {
        if (
          !t.profileId ||
          (Array.isArray(t.profileId) && t.profileId.length === 0)
        )
          return "Unknown";

        if (Array.isArray(t.profileId)) {
          const profileNames = t.profileId
            .map((id) => profileMap[id]?.name)
            .join(", ");
          return profileNames;
        }
        const profile = profileMap[t.profileId];
        return profile?.name;
      },
    },
    { key: "ref", header: "Reference Number" },
    {
      key: "profileId",
      header: "Currency",
      transform: async (t) => {
        const profileId = Array.isArray(t.profileId)
          ? t.profileId[0]
          : t.profileId;
        if (!profileId) return "-";
        try {
          const profile = await queryClient.fetchQuery({
            queryKey: ["profile", profileId],
            queryFn: () => fetchProfile(String(profileId)),
          });
          if (!profile?.currencyId) return "-";
          const currency = await queryClient.fetchQuery({
            queryKey: ["currency", profile.currencyId],
            queryFn: () => fetchCurrency(Number(profile.currencyId)),
          });
          return currency?.name ?? "-";
        } catch {
          return "-";
        }
      },
    },
    { key: "debit", header: "Debit" },
    { key: "credit", header: "Credit" },
    { key: "amount", header: "Balance" },
  ], [queryClient, profileMap]);

  function refresh() {
    setAppliedFilters((prev) => ({ ...prev }));
    queryClient.invalidateQueries();
  }

  const closeRegenerateDialog = () => {
    setRegenerateId(null);
    setRegenerateDate(null);
  };

  const handleRegenerate = async () => {
    if (!regenerateId) return;
    try {
      await regenerateSOASApi(regenerateId, regenerateDate);
      ToastNotification.success("SOA Regenerated successfully!");
      queryClient.invalidateQueries();
      closeRegenerateDialog();
    } catch (error: any) {
      console.error("Error regenerating SOA:", error);
      ToastNotification.error(error.message);
    }
  };
  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <SearchForm
          dateComponent={
            <CustomDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
            />
          }
          autoCompletes={autoCompletes}
          dropdowns={dropdowns}
          buttons={buttons}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="SOA List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search || ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "medium",
              variant: "outlined",
              borderColor: COLORS.GOLD,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.GOLD,
              label: "Generate SOA",
              permission: "POST_SOA",
            },
            {
              size: "medium",
              borderColor: COLORS.GOLD,
              bgColor: COLORS.GOLD,
              onClick: () => handleOpenDialog("send"),
              textColor: COLORS.WhiteBackground,
              label: "Send Email",
              permission: "SEND_SOA",
            },
          ]}
        />
        <CustomTable
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useSOAQuery}
          actions={actions}
          sortableFields={["debit", "ref", "amount", "createdDate", "credit"]}
        />
      </Box>
      <SOADialog
        ref={dialogRef}
        onRefresh={refresh}
        Accounts={accountOptions}
        Profiles={profileOptions}
        appliedFilters={appliedFilters}
      />
      <CustomDialog
        open={regenerateId !== null}
        onClose={closeRegenerateDialog}
        mode="edit"
        title="Regenerate SOA"
        fields={[
          {
            type: "custom",
            component: (
              <InitialDatePicker
                value={regenerateDate}
                onChange={setRegenerateDate}
              />
            ),
          },
        ]}
        buttons={[
          {
            label: "Regenerate",
            variant: "contained",
            bgColor: COLORS.GOLD,
            borderRadius: "12px",
            onClick: handleRegenerate,
            disabled: !hasJwtPermission("REGENERATE_SOA"),
          },
        ]}
      />
    </Box>
  );
};

export interface SOADialogRef {
  openDialog: (mode: "add" | "send" | "history") => Promise<void>;
}

interface SOADialogProps {
  onRefresh: () => void;
  Accounts: Option[];
  Profiles: Option[];
  appliedFilters: Params<SOAType & DateRange>;
}

const SOADialog = forwardRef<SOADialogRef, SOADialogProps>(
  ({ onRefresh, Profiles, appliedFilters }, ref) => {
    const queryClient = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<"add" | "send" | "history">(
      "add"
    );
    const [dialogData, setDialogData] = useState<Partial<DialogSOAData>>({});

    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [attachCcEmail, setAttachCcEmail] = useState<boolean>(true);

    useImperativeHandle(ref, () => ({
      openDialog: async (mode: "add" | "send" | "history") => {
        setDialogMode(mode);
        setErrors({});

        if (mode === "add") {
          setDialogData({
            profileId: undefined,
            initialDateSao: DEFAULT_INITIAL_DATE_SAO,
          });
          setIsSaveDisabled(true);
          setDialogOpen(true);
        } else {
          setSendDialogOpen(true);
        }
      },
      openSendDialog: () => {
        setDialogMode("send");
        setSendDialogOpen(true);
      },
    }));
    const closeDialog = () => {
      setDialogOpen(false);
      setDialogData({});
      setSendDialogOpen(false);
    };

    const handleSave = async () => {
      try {
        const profileIds: string[] = Array.isArray(dialogData.profileId)
          ? dialogData.profileId
          : dialogData.profileId
          ? [dialogData.profileId]
          : [];

        await createSOA(profileIds, dialogData.initialDateSao);

        ToastNotification.success("SOA Generated successfully!");

        onRefresh();
        setDialogOpen(false);
      } catch (error: any) {
        console.error("Error saving SOA:", error);
        ToastNotification.error(error.message);
      }
    };

    const sendEmail = async (selectedIds: string[], attachCcEmail: boolean) => {
      try {
        if (selectedIds.length === 1) {
          const id = selectedIds[0];
          await sendSingleSoaEmail(id, attachCcEmail);
          console.log(`Email sent for ID: ${id}`);
        } else if (selectedIds.length > 1) {
          await sendSoaEmails(selectedIds, attachCcEmail);
        }
        onRefresh();
        ToastNotification.success("Emails sent successfully!");
        closeDialog();
      } catch (error: any) {
        console.error("Error sending emails:", error);
      }
    };

    const isSendDisabled = selectedIds.length === 0;

    const fieldHistory = useMemo<IField<SOAType>[]>(() => [
      { key: "createdDate", header: "Created Date" },
      {
        key: "profileId",
        header: "Account Profile",
        // transform: async (t) => {
        //   if (
        //     !t.profileId ||
        //     (Array.isArray(t.profileId) && t.profileId.length === 0)
        //   )
        //     return "Unknown";

        //   if (Array.isArray(t.profileId)) {
        //     const profileNames = t.profileId
        //       .map((id) => profileMap[id]?.name)
        //       .join(", ");
        //     return profileNames;
        //   }
        //    cnst profile = profileMap[t.profileId];
        //   return profile?.name;
        // },
      },
      { key: "ref", header: "Reference Number" },
      {
        key: "profileId",
        header: "Currency",
        transform: async (t) => {
          const profileId = Array.isArray(t.profileId)
            ? t.profileId[0]
            : t.profileId;
          if (!profileId) return "-";
          try {
            const profile = await queryClient.fetchQuery({
              queryKey: ["profile", profileId],
              queryFn: () => fetchProfile(String(profileId)),
            });
            if (!profile?.currencyId) return "-";
            const currency = await queryClient.fetchQuery({
              queryKey: ["currency", profile.currencyId],
              queryFn: () => fetchCurrency(Number(profile.currencyId)),
            });
            return currency?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      { key: "debit", header: "Debit" },
      { key: "credit", header: "Credit" },
      { key: "amount", header: "Balance" },
      { key: "updatedBy", header: "Updated By" },
    ], [queryClient]);

    return (
      <>
        <CustomDialog
          open={dialogOpen}
          onClose={closeDialog}
          mode={dialogMode}
          title="Generate New SOA"
          fields={
            dialogMode === "history"
              ? [
                  {
                    type: "custom",
                    component: (
                      <History<SOAType>
                        filters={appliedFilters}
                        fields={fieldHistory}
                        fetchFunc={useSOAQuery}
                      />
                    ),
                  },
                ]
              : [
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Profile"
                        multiple={true}
                        selectAll={true}
                        hideChips={true}
                        value={dialogData.profileId || []}
                        options={Profiles.map((profile) => ({
                          label: profile.label,
                          value: String(profile.value),
                        }))}
                        onChange={(
                          value: string | number | (string | number)[]
                        ) => {
                          const valueArray: string[] = Array.isArray(value)
                            ? value.map((v) => String(v))
                            : value
                            ? [String(value)]
                            : [];
                          console.log("Updated profileId:", valueArray);
                          setDialogData((prev) => ({
                            ...prev,
                            profileId: valueArray,
                          }));
                          setIsSaveDisabled(valueArray.length === 0);
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.profileId}
                        placeholder="Select Profile"
                        highlightLabelStar={true}
                        helperText={
                          errors.profileId
                            ? "At least one profile must be selected"
                            : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <InitialDatePicker
                        value={dialogData.initialDateSao ?? null}
                        onChange={(date) =>
                          setDialogData((prev) => ({
                            ...prev,
                            initialDateSao: date,
                          }))
                        }
                      />
                    ),
                  },
                ]
          }
          buttons={[
            {
              label: "Generate",
              variant: "contained",
              bgColor: COLORS.GOLD,
              borderRadius: "12px",
              onClick: handleSave,
              disabled: isSaveDisabled || !hasJwtPermission("POST_SOA"),
            },
          ]}
        />
        {dialogMode === "send" && (
          <CustomDialog
            open={sendDialogOpen}
            onClose={closeDialog}
            mode={dialogMode}
            title="Send Email"
            fields={[
              {
                type: "custom",
                component: (
                  <SendSOAEmailDialog
                    onSelectedIdsChange={(ids) => setSelectedIds(ids)}
                    onAttachCcEmailChange={(value) => setAttachCcEmail(value)}
                  />
                ),
              },
            ]}
              buttons={[
              {
                label: selectedIds.length
                  ? `Send (${selectedIds.length})`
                  : "Send",
                variant: "contained",
                bgColor: COLORS.GOLD,
                borderRadius: "12px",
                onClick: () => sendEmail(selectedIds, attachCcEmail),
                disabled: isSendDisabled || !hasJwtPermission("SEND_SOA"),
              },
            ]}
          />
        )}
      </>
    );
  }
);

export default SOAPage;
